
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { validatePermission } from "../../../../../../../utils/services/validatePermission";
import EmailReportModal from "../../components/EmailReportModal/EmailReportModal";
import toasters from "../../../../../../../utils/toasters/Toasters";
import { Spinner } from "react-bootstrap";



const ReportFooter = ({
  BackText,
  PdfText,
  MailText,
  ExcelText,
  onPdfClick,
  onExcelClick,
  reportData,
  reportCode,
  reportTableData,
  fallbackData,
  disableDataCheck,
  generatePDFFunction,
  excelMode = 'api',      // 'api' | 'client'
  isHeavyReport = false,  // skip no-data check
}) => {
  const navigate = useNavigate()
  const { permissions } = useSelector((state) => state.auth)

  const [showEmailModal, setShowEmailModal] = useState(false)
  const [loading, setLoading] = useState({
    pdf: false,
    excel: false,
  })

  /* -------------------- Data Guards -------------------- */

  const effectiveData = reportTableData ?? fallbackData

  const hasNoData =
    !disableDataCheck &&
    !isHeavyReport &&
    (!effectiveData || effectiveData.length === 0)

  /* -------------------- Permissions -------------------- */

  const hasPdfPermission = validatePermission(
    permissions,
    'reports',
    'export_report'
  )

  const hasExcelPermission =
    excelMode === 'client'
      ? validatePermission(permissions, 'device_high_scoring', 'export')
      : validatePermission(permissions, 'reports', 'export_report')

  const hasMailPermission = validatePermission(
    permissions,
    'reports',
    'email_report'
  )

  /* -------------------- Handlers -------------------- */

  const handlePdfClick = async () => {
    if (hasNoData) {
      toasters({ type: 'warning', message: 'No data to export' })
      return
    }

    try {
      setLoading(prev => ({ ...prev, pdf: true }))
      await onPdfClick?.()
    } finally {
      setLoading(prev => ({ ...prev, pdf: false }))
    }
  }

  const handleExcelClick = async () => {
    if (hasNoData) {
      toasters({ type: 'warning', message: 'No data to export' })
      return
    }

    try {
      setLoading(prev => ({ ...prev, excel: true }))
      await onExcelClick?.()
    } finally {
      setLoading(prev => ({ ...prev, excel: false }))
    }
  }

  const handleMailClick = () => {
    if (hasNoData) {
      toasters({ type: 'warning', message: 'No data to export' })
      return
    }
    setShowEmailModal(true)
  }

  /* -------------------- Render -------------------- */

  return (
    <>
      <footer className="mt-4 w-100">
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">

          {/* Back Button */}
          {BackText && (
            <button
              onClick={() => navigate(-1)}
              className="btn btn-outline-secondary"
            >
              {BackText}
            </button>
          )}

          {/* Action Buttons */}
          <div className="d-flex gap-2">
            {/* PDF */}
            {PdfText && hasPdfPermission && (
              <button
                className="btn btn-primary"
                disabled={loading.pdf}
                onClick={handlePdfClick}
              >
                {loading.pdf && (
                  <Spinner
                    className="me-2"
                    as="span"
                    animation="border"
                    size="sm"
                  />
                )}
                {PdfText}
              </button>
            )}

            {/* Mail */}
            {MailText && hasMailPermission && (
              <button
                className="btn btn-primary"
                onClick={handleMailClick}
              >
                {MailText}
              </button>
            )}

            {/* Excel */}
            {ExcelText && hasExcelPermission && (
              <button
                className="btn btn-primary"
                disabled={loading.excel}
                onClick={handleExcelClick}
              >
                {loading.excel && (
                  <Spinner
                    className="me-2"
                    as="span"
                    animation="border"
                    size="sm"
                  />
                )}
                {ExcelText}
              </button>
            )}
          </div>
        </div>
      </footer>

      {/* Email Modal Placeholder (unchanged) */}
      {showEmailModal && (
        <></>
      )}

            <EmailReportModal
        show={showEmailModal}
        onHide={() => setShowEmailModal(false)}
        reportData={reportData}
        reportCode={reportCode}
        reportTableData={effectiveData}
        generatePDFFunction={generatePDFFunction}
        //pdfPayload={cachedPdf} // pass cached PDF
      />
    </>
  )
}

export default ReportFooter